import React, { useState } from 'react'
import TopBar from './TopBar'
import Stepper from './Stepper'
import AccountStep from './steps/AccountStep'
import SportSchoolStep from './steps/SportSchoolStep'
import IdentityStep from './steps/IdentityStep'
import NILProfileStep from './steps/NILProfileStep'
import StoryStep from './steps/StoryStep'
import { supabase } from '../lib/supabase'
import athleteService from '../services/athleteService'

const AthleteRegistration = () => {
  const [currentStep, setCurrentStep] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [formData, setFormData] = useState({
    // Account info
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    password: '',
    confirmPassword: '',
    agents: [],
    
    // Sport & School
    sport: '',
    division: '',
    conference: '',
    school: '',
    graduationYear: '',
    eligibility: '',
    jerseyNumber: '',
    position: '',
    athleticDepartmentContact: '',
    athleticDepartmentEmail: '',
    
    // Identity verification
    idFront: null,
    idBack: null,
    selfie: null,
    liveness: null,
    identityVerified: false,
    
    // NIL Profile
    displayName: '',
    bio: '',
    socialMedia: {},
    achievements: [],
    nilAssets: [],
    
    // Story Protocol
    storyWalletAddress: '',
    ipRegistered: false,
    transactionHash: ''
  })

  const steps = [
    { id: 0, name: 'Account', title: 'Create Your IPST Account' },
    { id: 1, name: 'Sport & School', title: 'Sport, Conference & School' },
    { id: 2, name: 'Identity Verify', title: 'Identity Verification' },
    { id: 3, name: 'NIL Profile', title: 'NIL Profile' },
    { id: 4, name: 'Story $IP', title: 'Story Protocol IP' }
  ]

  const goStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex)
    }
  }

  const updateFormData = (newData) => {
    setFormData(prev => ({ ...prev, ...newData }))
  }

  const uploadFile = async (file, athleteId, fileType, description = '') => {
    if (!file) return null

    const fileName = `${athleteId}/${Date.now()}-${file.name}`
    const { data, error } = await supabase.storage
      .from('athlete-media')
      .upload(fileName, file)

    if (error) throw error

    // Save file record to database
    const { data: mediaRecord, error: mediaError } = await supabase
      .from('athlete_media')
      .insert({
        athlete_id: athleteId,
        file_type: fileType,
        file_name: file.name,
        file_path: data.path,
        file_size: file.size,
        mime_type: file.type,
        description: description,
        upload_status: 'completed'
      })
      .select()
      .single()

    if (mediaError) throw mediaError
    return mediaRecord
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      // 1. Create athlete record using service account
      const athlete = await athleteService.createAthlete(formData)

      // 2. Upload identity verification documents
      if (formData.idFront) {
        await athleteService.uploadFile(formData.idFront, athlete.id, 'document', 'ID Front')
      }
      if (formData.idBack) {
        await athleteService.uploadFile(formData.idBack, athlete.id, 'document', 'ID Back')
      }
      if (formData.selfie) {
        await athleteService.uploadFile(formData.selfie, athlete.id, 'image', 'Selfie')
      }

      // 3. Upload NIL media files (photo, video, voice)
      if (formData.photoFile) {
        await athleteService.uploadFile(formData.photoFile, athlete.id, 'image', 'NIL Official Photo')
      }
      if (formData.videoFile) {
        await athleteService.uploadFile(formData.videoFile, athlete.id, 'video', 'NIL Head Movement Video')
      }
      if (formData.voiceFile) {
        await athleteService.uploadFile(formData.voiceFile, athlete.id, 'audio', 'NIL Voice Signature')
      }

      // 4. Save social media links
      await athleteService.saveSocialMedia(athlete.id, formData.socialMedia)

      // 5. Save achievements
      await athleteService.saveAchievements(athlete.id, formData.achievements)

      // 6. Upload NIL assets
      if (formData.nilAssets && formData.nilAssets.length > 0) {
        for (const asset of formData.nilAssets) {
          if (asset.file) {
            await athleteService.uploadFile(asset.file, athlete.id, asset.type || 'image', asset.title)
          }
        }
      }

      // 7. Create auth user
      await athleteService.createAuthUser(formData.email, formData.password, athlete.id, formData)

      setSubmitSuccess(true)
      console.log('Athlete registration completed successfully:', athlete.id)

    } catch (error) {
      console.error('Registration error:', error)
      setSubmitError(error.message || 'Failed to complete registration')
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return <AccountStep data={formData} onChange={updateFormData} onNext={() => goStep(1)} />
      case 1:
        return <SportSchoolStep data={formData} onChange={updateFormData} onNext={() => goStep(2)} onBack={() => goStep(0)} />
      case 2:
        return <IdentityStep data={formData} onChange={updateFormData} onNext={() => goStep(3)} onBack={() => goStep(1)} />
      case 3:
        return <NILProfileStep data={formData} onChange={updateFormData} onNext={() => goStep(4)} onBack={() => goStep(2)} />
      case 4:
        return <StoryStep 
          data={formData} 
          onChange={updateFormData} 
          onBack={() => goStep(3)} 
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          submitError={submitError}
          submitSuccess={submitSuccess}
        />
      default:
        return <AccountStep data={formData} onChange={updateFormData} onNext={() => goStep(1)} />
    }
  }

  return (
    <div className="min-h-screen bg-off">
      <TopBar />
      <Stepper 
        steps={steps} 
        currentStep={currentStep} 
        onStepClick={goStep} 
      />
      
      <div className="main" style={{ maxWidth: '880px', margin: '0 auto', padding: '40px 20px 80px' }}>
        {renderStep()}
      </div>
    </div>
  )
}

export default AthleteRegistration
